// ### A11Y
//
// This module adds generic accessibility features
// to all pages.
//
// #### Focus Styling Support
//
// To avoid the visual clutter of focus visibility
// on elements such as buttons, this module adds
// the class `a11y-nofocus` to the `<body>`
// element. CSS may then use this class to disable
// focus visibility. The module then listens for
// Tab (and Shift-Tab) key presses, which indicates
// a keyboard user. When it detects such a key press,
// it removes the class.
//
// In addition, when the `a11y-nofocus` class is
// removed, the `data-keyboard-user` attribute is
// set to `"true"`, affirmatively indicating that
// a keyboard user is active.
//
// #### Focus Trapping
//
// The module includes utilities for trapping focus
// within a container element. This is important,
// for example, when a modal dialog is visible.
// Focus should be trapped within that dialog.
//
// The API for this functionality:
//
//  - `Intellum.a11y.trapFocus(el, initialFocus)`
//  - `Intellum.a11y.releaseFocus()`
//
// The methods' parameters:
//
//  - `el` the element within which focus should remain,
//    optionally an array of elements.
//  - `initialFocus` [optional] a child element within
//    the container that should receive the intial focus.
//    If omitted, the first focusable element within the
//    the container is used.
//
// Instead of locking down tabbing among certain elements
// as it formerly did, the `#trapFocus` method
// locks down only the first and last focusable elements
// in the dialog (so that tabbing can loop around), but
// otherwise lets tabbing progress natively to the next
// tabbable element, and the tabbing can adapt as content
// is hidden or shown. Note: this does require that
// the first and last focusable elements in a dialog are
// never hidden.
//
// #### Focus Concealment
//
// When a set of elements exist in the DOM but are
// not visible on the page (e.g. a sliding navigation
// drawer), those elements should not be able to
// receive keyboard focus. This module includes
// utilties to exclude those sections from the
// keyboard navigation and then restore them.
//
//  - `Intellum.a11y.excludeFocus(el)`
//  - `Intellum.a11y.includeFocus(el)`
//
// The `el` parameter is the container elements or
// an array of container elements.

;(function(document, window) {

	'use strict';

    // Use the `Intellum` namespace.
    window.Intellum = window.Intellum || {};

    window.Intellum.a11y = (function() {

        // State variables
        var focusParents = null,
            focusable = null,
            prevFocus = null,
            boundKeydownHandler;

        var focusableSelector = [
            'a[href]', 'area[href]', 'input:not([disabled]):not([type="hidden"]):not(.hidden)',
            'select:not([disabled])', 'textarea:not([disabled])',
            'button:not([disabled]):not(.hidden)', 'iframe', 'object', 'embed',
            '*[tabindex="0"]', '*[contenteditable]'
        ].join(', ');

        // Utility function to check if elements array contains a child
        function isChild(els, child) {
            return els.some(function(el) {
                return el.contains(child);
            });
        }

        // Handle key events
        function handleKey(escFunc, els, ev) {

            // Watch for Tab keys
            if (ev.key === "Tab") {
                var currentIdx = focusable.indexOf(document.activeElement);

                /* Only control first and last tab stop,
                   let native tabbing handle the rest;
                   This accounts for content that may appear and disappear from
                   view
                */

                // If the focus "escaped" thecontainer, bring it back.
                if (focusable.length > 0 && !isChild(els, document.activeElement)) {
                    focusable[0].focus();
                } else if (ev.shiftKey) {
                    if (currentIdx === 0) {
                        focusable[focusable.length - 1].focus();
                        ev.preventDefault();
                    }
                } else {
                    if (currentIdx === (focusable.length - 1)) {
                        focusable[0].focus();
                        ev.preventDefault();
                    }
                }

            // Make Esc key perform close function if provided
            } else if (escFunc && typeof escFunc === "function" && (event.key === 'Escape' || event.key === 'Esc')) {
                escFunc();
            }
        }

        // Coerce an element to an array of elements
        function forceArray(els) {
            // If no input parameter supplied, use the whole body
            if (els === undefined) {
                els = document.body;
            }

            // Force the input parameter to a true array.
            if (!Array.isArray(els)) {
                if (els.length) {
                    els = Array.prototype.slice.call(els);
                } else {
                    els = [ els ];
                }
            }
            return els;
        }

        // Get all focusable elements that are children
        // of an element (including the element itself),
        // Input can also be array of elements.
        function getFocusable(els) {
            return forceArray(els).reduce(function(focusable, el) {
                return focusable.concat(
                    el.matches(focusableSelector) ? [el] : [],
                    Array.prototype.slice.call(el.querySelectorAll(focusableSelector))
                );
            }, []);
        }

        // Conceal focusable elements by excluding them from
        // keyboard navigation flow.
        function excludeFocus(els) {
            getFocusable(forceArray(els)).forEach(function(el) {
                el.prevTabindex = el.getAttribute('tabindex');
                el.setAttribute('tabindex', '-1');
            });
        }

        // Restore excluded focusable elements
        function includeFocus(els) {
            getFocusable(forceArray(els)).forEach(function(el) {
                if (el.getAttribute('tabindex') === '-1') {
                    if (el.prevTabindex) {
                        el.setAttribute('tabindex', el.prevTabindex);
                    } else {
                        el.removeAttribute('tabindex');
                    }
                }
            });
        }

        // Trap the focus within an element
        function trapFocus(els, initialFocus, escFunc) {
            focusParents = els = forceArray(els);

            // Remember which element has current focus
            prevFocus = document.activeElement;

            // Get a list of all the focusable
            // elements within the container
            // so we can make sure that focus
            // is constrained to that set.
            focusable = getFocusable(els);

            // If the current focussed element
            // isn't within the container, make
            // it so by setting focus to the specified
            // element or the first focusable element.
            initialFocus = initialFocus ||
                (isChild(els, document.activeElement) && document.activeElement) ||
                (focusable.length > 0 && focusable[0]);

            // We may need to wait on a transition end
            // before setting the initial focus. Safari
            // will scroll to bring the initially focused
            // element into the viewport, even if such
            // scrolling wouldn't be required after the
            // transition ends.
            var transitionEl;

            els.some(function(el) {
                var styles = window.getComputedStyle(el),
                    durationString = styles.getPropertyValue('transition-duration'),
                    duration = durationString && durationString.split(',').
                        some(function(prop) { return prop && window.parseInt(prop) !== 0; }),
                    delayString = styles.getPropertyValue('transition-delay'),
                    delay = delayString && delayString.split(',').
                        some(function(prop) { return prop && window.parseInt(prop) !== 0; });
                if (duration || delay) {
                    transitionEl = el;
                    return true;
                }
            });

            function setInitialFocus() {
                transitionEl.removeEventListener('transitionend', setInitialFocus);
                initialFocus.focus();
            }

            if (initialFocus) {
                if (transitionEl && document.body.style.transition !== 'undefined') {
                    transitionEl.addEventListener('transitionend', setInitialFocus);
                } else {
                    initialFocus.focus();
                }
            }

            // Install our keyboard handler
            if (focusable.length > 0) {
                boundKeydownHandler = handleKey.bind(this, escFunc, els);
                document.addEventListener('keydown', boundKeydownHandler);
            }

        }

        // Release trapped focus
        function releaseFocus() {

            // Remove our keyboard handler
            document.removeEventListener("keydown", boundKeydownHandler);

            // Restore focus
            if (prevFocus && prevFocus.focus) {
                prevFocus.focus();
            }

            // Reset state
            focusParents = null;
            focusable = null;
            prevFocus = null;
        }

        // Public API
        return {
            excludeFocus: excludeFocus,
            includeFocus: includeFocus,
            releaseFocus: releaseFocus,
            trapFocus:    trapFocus
        };

    })();

	function initA11y() {

		var body = document.body;

		// Since this module is running, it can
		// detect keyboard users. Until such a
		// user is detected, no need for focus
		// highlights. Add the appropiate class.
        if (body.className.indexOf('a11y-nofocus') === -1) {
		    body.className += ' a11y-nofocus';
        }

		// Now look for keydown events. If we detect
		// a tab key, assume a keyboard user and
		// re-enable focus hightlight.
		document.addEventListener('keydown', function(ev) {
			if (ev.key === 'Tab') {
				body.className = body.className.replace(' a11y-nofocus','');
                body.setAttribute('data-keyboard-user', 'true');
			}
		});
	}

    // Wait until the document is ready for initialization
    window.Intellum.util.ready(initA11y);

})(window.document, window);
